// Preview responsivo (Onda 2.C).
// Reutiliza EXATAMENTE a rota do membro via iframe apontando para
// /membros/meu-projeto/missao/{id}?preview=1. O toggle de dispositivo
// apenas altera a moldura CSS — o iframe NÃO recarrega, o scroll e o
// estado do membro são preservados. Comportamento readonly permanece:
// autosave, respostas e finalize são noop em ?preview=1.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft, Monitor, Tablet, Smartphone, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/admin/AdminPrimitives";

type Device = "desktop" | "tablet" | "mobile";

export const Route = createFileRoute("/_authenticated/admin/conteudos/jornadas/preview/$missionId")({
  head: () => ({ meta: [
    { title: "Preview da missão — Admin" },
    { name: "robots", content: "noindex, nofollow" },
  ]}),
  component: PreviewPage,
});

const FRAME: Record<Device, { w: number; h: number; label: string; icon: any }> = {
  desktop: { w: 1280, h: 800, label: "Desktop", icon: Monitor },
  tablet: { w: 820, h: 1100, label: "Tablet", icon: Tablet },
  mobile: { w: 390, h: 780, label: "Mobile", icon: Smartphone },
};

function PreviewPage() {
  const { missionId } = Route.useParams();
  const [device, setDevice] = useState<Device>("desktop");

  // Stable src — set once, never changes, so device toggle does NOT reload.
  const src = useMemo(
    () => `/membros/meu-projeto/missao/${missionId}?preview=1`,
    [missionId],
  );

  const cfg = FRAME[device];

  return (
    <div>
      <PageHeader
        title="Preview da missão"
        description="Reutiliza a área do membro em modo readonly (nada é salvo)."
        back={{ to: "..", label: "Voltar" }}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-black/[0.06] bg-white px-4 py-2.5">
        <div className="inline-flex items-center gap-1 rounded-lg bg-[var(--gray-soft)] p-1">
          {(Object.keys(FRAME) as Device[]).map((d) => {
            const Ico = FRAME[d].icon;
            const active = device === d;
            return (
              <button
                key={d}
                onClick={() => setDevice(d)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  active ? "bg-white text-[var(--navy)] shadow-sm" : "text-[var(--gray-ink)] hover:text-[var(--navy)]"
                }`}
              >
                <Ico size={13} /> {FRAME[d].label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--gray-ink)]">
          <span>{cfg.w} × {cfg.h}</span>
          <Link
            to="/membros/meu-projeto/missao/$id"
            params={{ id: missionId }}
            search={{ preview: 1 } as never}
            target="_blank"
            className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1 font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
          >
            <ExternalLink size={11} /> Abrir em nova aba
          </Link>
        </div>
      </div>

      <div className="grid place-items-center rounded-2xl border border-dashed border-black/10 bg-[var(--gray-soft)]/40 p-6">
        <div
          className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm transition-[width,height] duration-300 ease-out"
          style={{ width: cfg.w, maxWidth: "100%", height: cfg.h }}
        >
          <iframe
            src={src}
            title="Preview do membro"
            className="h-full w-full border-0"
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
      </div>

      <p className="mt-3 text-center text-[11px] text-[var(--gray-ink)]">
        Alternar entre dispositivos apenas altera a moldura — o iframe não recarrega,
        o scroll e o estado internos permanecem.
      </p>
    </div>
  );
}
